import React from 'react'
import styled from 'styled-components'
import { Box, Typography } from '@mui/material'
import { FoodArea } from '../../../../store/foodAreaSlice'
import { DEFAULT_CARD_IMAGE } from '../../../../helpers/static-constants'

// ─── Tag metadata ─────────────────────────────────────────────────────────────

interface TagMetadata {
  backgroundColor: string
  textColor: string
  accentColor: string
  badgeLabel: string
  emoji: string
  /** Experience-first title shown on non-LOCAL_FAVORITE cards */
  title: string
  subtitle: string
  cta: string
  /** LOCAL_FAVORITE uses the neighborhood name as the title instead */
  isNeighborhoodTitle: boolean
}

const TAG_METADATA: Record<string, TagMetadata> = {
  MOST_WALKABLE: {
    backgroundColor: '#F5E6D3',
    textColor: '#2C2416',
    accentColor: '#B5722E',
    badgeLabel: 'Walkable',
    emoji: '🚶',
    title: 'Perfect for a Food Crawl',
    subtitle: 'Great spots close enough to walk between',
    cta: 'Plan your route',
    isNeighborhoodTitle: false,
  },
  MOST_OPTIONS: {
    backgroundColor: '#E8F4F8',
    textColor: '#1A3A40',
    accentColor: '#2A6478',
    badgeLabel: 'Most Variety',
    emoji: '🍽️',
    title: 'Something for Everyone',
    subtitle: 'The widest range of cuisines and price points in one area',
    cta: 'Browse options',
    isNeighborhoodTitle: false,
  },
  LOCAL_FAVORITE: {
    backgroundColor: '#E6F4E6',
    textColor: '#2E5F34',
    accentColor: '#3A7A41',
    badgeLabel: 'Local Gem',
    emoji: '💎',
    // title and subtitle are overridden at render time for LOCAL_FAVORITE
    title: '',
    subtitle: 'Underrated spots the locals keep coming back to',
    cta: 'Discover spots',
    isNeighborhoodTitle: true,
  },
  BEST_VALUE: {
    backgroundColor: '#FEE8DC',
    textColor: '#6B3E2E',
    accentColor: '#9A4E2A',
    badgeLabel: 'Best Value',
    emoji: '💸',
    title: 'Great Food, Fair Price',
    subtitle: "Where you get the most for your money around here",
    cta: 'Find deals',
    isNeighborhoodTitle: false,
  },
  TRENDING: {
    backgroundColor: '#F0E6F6',
    textColor: '#3D2E5F',
    accentColor: '#634F9C',
    badgeLabel: 'Trending',
    emoji: '🔥',
    title: "What's Hot Right Now",
    subtitle: 'The spots getting the most attention this week',
    cta: "See what's hot",
    isNeighborhoodTitle: false,
  },
}

const DEFAULT_METADATA: TagMetadata = {
  backgroundColor: '#F5F5F0',
  textColor: '#2A2A2A',
  accentColor: '#777',
  badgeLabel: 'Neighborhood',
  emoji: '📍',
  title: 'Explore the Area',
  subtitle: 'Great restaurants all within walking distance',
  cta: 'Explore spots',
  isNeighborhoodTitle: false,
}

const getMeta = (tagType: string): TagMetadata =>
  TAG_METADATA[tagType?.toUpperCase()] ?? DEFAULT_METADATA

const isRealImage = (url: string | undefined): url is string =>
  Boolean(url) && url !== DEFAULT_CARD_IMAGE

// ─── Styled components ────────────────────────────────────────────────────────

const CardContainer = styled(Box)<{ $bgColor: string }>`
  background-color: ${(p) => p.$bgColor};
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  user-select: none;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  }
`

/* 2/3 + 1/3 editorial image grid */
const ImageGrid = styled(Box)`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
  height: 96px;
  flex-shrink: 0;
  overflow: hidden;
  gap: 2px;
`

const MainImage = styled.img`
  grid-column: 1;
  grid-row: 1 / 3;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const SecondaryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const ImagePlaceholder = styled(Box)<{ $color: string }>`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    ${(p) => p.$color}22,
    ${(p) => p.$color}44
  );
`

/* No-image fallback for LOCAL_FAVORITE: show neighborhood name in image area */
const NeighborhoodStrip = styled(Box)<{ $bgColor: string; $textColor: string }>`
  height: 96px;
  background: linear-gradient(
    135deg,
    ${(p) => p.$bgColor}cc,
    ${(p) => p.$bgColor}
  );
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 0 18px 14px;
  flex-shrink: 0;
`

const NeighborhoodStripName = styled(Typography)<{ $textColor: string }>`
  && {
    font-size: 22px;
    font-weight: 800;
    line-height: 1.15;
    color: ${(p) => p.$textColor};
    letter-spacing: -0.5px;
    opacity: 0.9;
  }
`

const NeighborhoodStripCity = styled(Typography)<{ $textColor: string }>`
  && {
    font-size: 11px;
    font-weight: 500;
    color: ${(p) => p.$textColor};
    opacity: 0.5;
    margin-top: 2px;
  }
`

/* No-image fallback for other types: emoji strip */
const EmojiStrip = styled(Box)<{ $color: string }>`
  height: 96px;
  background: linear-gradient(135deg, ${(p) => p.$color}18, ${(p) => p.$color}38);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  flex-shrink: 0;
`

/* Text section */
const TextSection = styled(Box)`
  padding: 10px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
`

const Badge = styled(Box)<{ $textColor: string }>`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => p.$textColor};
  opacity: 0.5;
  margin-bottom: 1px;
`

const CardTitle = styled(Typography)<{ $textColor: string; $large?: boolean }>`
  && {
    font-size: ${(p) => (p.$large ? '17px' : '15px')};
    font-weight: 700;
    line-height: 1.2;
    color: ${(p) => p.$textColor};
    letter-spacing: ${(p) => (p.$large ? '-0.3px' : '-0.2px')};
  }
`

const CardSubtitle = styled(Typography)<{ $textColor: string }>`
  && {
    font-size: 12px;
    line-height: 1.5;
    color: ${(p) => p.$textColor};
    opacity: 0.62;
    margin-top: 1px;
  }
`

const Footer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
`

const FooterMeta = styled(Typography)<{ $textColor: string }>`
  && {
    font-size: 11px;
    color: ${(p) => p.$textColor};
    opacity: 0.4;
    line-height: 1;
  }
`

const CtaLabel = styled(Typography)<{ $textColor: string }>`
  && {
    font-size: 11.5px;
    font-weight: 600;
    color: ${(p) => p.$textColor};
    opacity: 0.78;
    line-height: 1;
  }
`

// ─── Component ────────────────────────────────────────────────────────────────

interface FoodAreaCardProps {
  area: FoodArea
  previewImages?: string[]
  onClick?: () => void
}

const FoodAreaCard: React.FC<FoodAreaCardProps> = ({
  area,
  previewImages = [],
  onClick,
}) => {
  const meta = getMeta(area.tagType)
  const realImages = previewImages.filter(isRealImage).slice(0, 3)
  const hasImages = realImages.length > 0

  const countLabel =
    area.restaurantCount > 0
      ? `${area.restaurantCount} spot${area.restaurantCount !== 1 ? 's' : ''}`
      : null

  // For LOCAL_FAVORITE: title = neighborhood name, footer shows city
  // For others: title = experience title, footer shows neighborhood
  const isLocal = meta.isNeighborhoodTitle
  const displayTitle = isLocal ? area.name : meta.title
  const footerLeft = isLocal
    ? [area.city, countLabel].filter(Boolean).join(' · ')
    : [area.name !== area.city ? area.name : null, countLabel]
        .filter(Boolean)
        .join(' · ') || area.city

  const renderImageSection = (): React.ReactElement => {
    if (hasImages) {
      return (
        <ImageGrid>
          <MainImage src={realImages[0]} alt="" loading="lazy" />
          {realImages[1] ? (
            <SecondaryImage src={realImages[1]} alt="" loading="lazy" />
          ) : (
            <ImagePlaceholder $color={meta.accentColor} />
          )}
          {realImages[2] ? (
            <SecondaryImage src={realImages[2]} alt="" loading="lazy" />
          ) : (
            <ImagePlaceholder $color={meta.accentColor} />
          )}
        </ImageGrid>
      )
    }

    if (isLocal) {
      return (
        <NeighborhoodStrip
          $bgColor={meta.accentColor}
          $textColor={meta.textColor}
        >
          <NeighborhoodStripName $textColor={meta.textColor}>
            {area.name}
          </NeighborhoodStripName>
          {area.city && area.city !== area.name && (
            <NeighborhoodStripCity $textColor={meta.textColor}>
              {area.city}
            </NeighborhoodStripCity>
          )}
        </NeighborhoodStrip>
      )
    }

    return <EmojiStrip $color={meta.accentColor}>{meta.emoji}</EmojiStrip>
  }

  return (
    <CardContainer
      $bgColor={meta.backgroundColor}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.()
      }}
    >
      {renderImageSection()}

      <TextSection>
        <Badge $textColor={meta.textColor}>
          {meta.emoji}&nbsp;{meta.badgeLabel}
        </Badge>

        <CardTitle $textColor={meta.textColor} $large={isLocal}>
          {displayTitle}
        </CardTitle>

        <CardSubtitle $textColor={meta.textColor}>{meta.subtitle}</CardSubtitle>

        <Footer>
          <FooterMeta $textColor={meta.textColor}>{footerLeft}</FooterMeta>
          <CtaLabel $textColor={meta.textColor}>{meta.cta}&nbsp;→</CtaLabel>
        </Footer>
      </TextSection>
    </CardContainer>
  )
}

export default FoodAreaCard
